import { useCallback, useMemo, useRef, useState } from "react";
import {
  fetchTvRailPage,
  type TvCatalogueHome,
  type TvCatalogueItem,
  type TvLocale,
  type TvRail,
} from "../../../hooks/useTvCatalogue";

/**
 * Rails that grow as a guest arrows towards the end of one (`TV-3`).
 *
 * ## Why a rail is paged at all
 *
 * The home read carries page 1 of every rail and nothing more. A premise with a
 * thousand licensed titles would otherwise make the first screen of the evening
 * the slowest one, on the hardware least able to afford it — and a guest sees
 * about four tiles at a time, so the other nine hundred and ninety-six are bytes
 * nobody looks at.
 *
 * ## ⚠️ The trap this module exists to avoid
 *
 * **An empty FIRST page means the rail is unavailable. An empty LATER page means
 * the end was reached.** They are the same JSON. Printing "nothing here" for the
 * second case — under a rail a guest has just arrowed to the end of — is a bug
 * report waiting to be filed; rendering the rail anyway for the first is a named
 * heading with a void under it.
 *
 * So: page 1 comes from the home read and its emptiness is the rail's own
 * verdict (the screen drops such a rail entirely); every later page only ever
 * ADDS, and an empty one silently sets `hasMore` false. There is no third
 * behaviour, and neither case ever removes a tile a guest can already see.
 */

/**
 * How close to the end focus must get before the next page is asked for.
 *
 * Two tiles, which at four visible is roughly half a screen of warning. Larger
 * fetches pages a guest never reaches; smaller and the rail stops dead under
 * their thumb while the request is still in flight.
 */
export const PREFETCH_WITHIN = 2;

/** Backoff before a rail whose page request failed is asked again. */
export const RAIL_RETRY_MS = 10_000;

/** A rail as the screen draws it: page 1 plus whatever has been added since. */
export interface RailView {
  carouselId: string;
  title: string;
  items: TvCatalogueItem[];
  /** A further page is believed to exist. */
  hasMore: boolean;
  /** A page is on the wire right now — the tail may still grow. */
  loading: boolean;
}

interface Extra {
  items: TvCatalogueItem[];
  /** The highest page successfully merged. */
  page: number;
  hasMore: boolean;
}

/**
 * Merge the home read's page 1 with the pages fetched since, and expose the one
 * call that asks for more.
 *
 * `reach(carouselId, index)` takes the index focus has moved to. It is
 * idempotent and cheap: it fetches only when that index is within
 * {@link PREFETCH_WITHIN} of the end, the rail believes more exists, nothing is
 * already in flight for it, and any previous failure has cooled off.
 */
export function useRailPages(
  home: TvCatalogueHome | undefined,
  loc: TvLocale
): { rails: RailView[]; reach: (carouselId: string, index: number) => void } {
  // ⚠️ `extraRef` and `inFlight` are the truth; the two `useState`s exist only
  // so a render can see them.
  //
  // The obvious shape — one piece of state, mirrored into a ref by an effect —
  // shipped a DOUBLE FETCH, and the mechanism is worth writing down because it
  // is not obvious. `reach` is called from a CHILD's effect, React runs child
  // effects before parent effects, so the parent's mirroring effect ran
  // afterwards and wrote the render's stale `[]` back over the guard the child
  // had just set. The next render called `reach` again, found no guard, and
  // asked for page 2 a second time — which appends it twice, and a guest sees
  // every title on the tail of the rail duplicated. A ref that is only ever
  // written where the decision is made cannot go stale that way.
  const [extra, setExtra] = useState<Record<string, Extra>>({});
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  const extraRef = useRef<Record<string, Extra>>({});
  const inFlight = useRef<Set<string>>(new Set());
  const coolOff = useRef<Record<string, number>>({});
  /**
   * Which library the pages in flight belong to.
   *
   * The home read re-polls on its own timer, so a page request can be on the
   * wire when a NEW library arrives underneath it. Without this, that answer
   * would be appended to the rail it no longer belongs to — yesterday's page 2
   * grafted onto today's rail — and its `finally` would clear the in-flight
   * guard the new generation had just set, which is the double fetch again.
   */
  const generation = useRef(0);
  const seen = useRef<{
    rails: TvRail[] | undefined;
    locale: string;
    defaultLocale: string;
  } | null>(null);
  const { locale, defaultLocale } = loc;
  const homeRails = home?.rails;

  /**
   * A new home read, or a language switch, invalidates everything appended so
   * far: the tail would otherwise be page 2 of yesterday's rail, or four tiles
   * still labelled in the language the guest has just left.
   *
   * ⚠️ **Done DURING RENDER, not in an effect**, and for the same reason the
   * guards above are refs. React flushes child effects before parent ones, so a
   * reset in a parent effect ran AFTER `reach` had already fired from the
   * child's effect — which meant the first prefetch of every home read was
   * issued under the old generation, discarded by the guard, and then issued
   * again. One wasted round trip per rail per poll, and a flicker of the
   * "more coming" placeholder each time. Resetting where the change is first
   * observed makes the child's very first `reach` belong to the right
   * generation.
   *
   * Setting this component's own state during its own render is React's
   * documented escape hatch for "reset state when a prop changes"; the identity
   * check makes it idempotent, so it cannot loop.
   */
  if (
    !seen.current ||
    seen.current.rails !== homeRails ||
    seen.current.locale !== locale ||
    seen.current.defaultLocale !== defaultLocale
  ) {
    seen.current = { rails: homeRails, locale, defaultLocale };
    generation.current += 1;
    extraRef.current = {};
    inFlight.current = new Set();
    coolOff.current = {};
    if (Object.keys(extra).length > 0) setExtra({});
    if (loadingIds.length > 0) setLoadingIds([]);
  }

  const rails = useMemo<RailView[]>(
    () =>
      (homeRails ?? []).map((rail) => {
        const more = extra[rail.carouselId];
        return {
          carouselId: rail.carouselId,
          title: rail.title,
          items: more ? [...rail.items, ...more.items] : rail.items,
          hasMore: more ? more.hasMore : rail.hasMore,
          loading: loadingIds.includes(rail.carouselId),
        };
      }),
    [homeRails, extra, loadingIds]
  );

  const reach = useCallback(
    (carouselId: string, index: number) => {
      // Read off the home read plus the refs rather than off the merged view:
      // `reach` is recreated whenever `homeRails` changes, so the closure is
      // never stale, and the refs carry everything appended since.
      const base = homeRails?.find((r) => r.carouselId === carouselId);
      if (!base) return;
      const held = extraRef.current[carouselId];
      const length = base.items.length + (held?.items.length ?? 0);
      const hasMore = held ? held.hasMore : base.hasMore;
      if (!hasMore) return;
      if (index < length - 1 - PREFETCH_WITHIN) return;
      if (inFlight.current.has(carouselId)) return;
      if (Date.now() < (coolOff.current[carouselId] ?? 0)) return;

      const next = (held?.page ?? base.page) + 1;
      const mine = generation.current;
      inFlight.current.add(carouselId);
      setLoadingIds((prev) =>
        prev.includes(carouselId) ? prev : [...prev, carouselId]
      );

      void (async () => {
        try {
          const page = await fetchTvRailPage(carouselId, next, {
            locale,
            defaultLocale,
          });
          const items = page?.items ?? [];
          // The library changed while this was on the wire. Dropped on the
          // floor rather than grafted onto a rail it is not part of.
          if (generation.current !== mine) return;
          const prior = extraRef.current[carouselId];
          const merged: Extra = {
            items: [...(prior?.items ?? []), ...items],
            page: next,
            // ⚠️ THE RULE. An empty LATER page is the end of the rail, never
            // "this rail is empty": the tiles already on screen stay exactly
            // where they are and only `hasMore` changes.
            hasMore: items.length > 0 && (page?.hasMore ?? false),
          };
          extraRef.current = { ...extraRef.current, [carouselId]: merged };
          setExtra(extraRef.current);
        } catch {
          // Not the end — a blip. `hasMore` is left alone so the rail can grow
          // again once the cool-off passes; ending it here would truncate a
          // guest's library because of one dropped packet.
          if (generation.current === mine) {
            coolOff.current[carouselId] = Date.now() + RAIL_RETRY_MS;
          }
        } finally {
          // Only the generation that SET the guard may clear it. A stale
          // request clearing a fresh one's guard is the double fetch again,
          // just with a slower fuse.
          if (generation.current === mine) {
            inFlight.current.delete(carouselId);
            setLoadingIds((prev) => prev.filter((id) => id !== carouselId));
          }
        }
      })();
    },
    [homeRails, locale, defaultLocale]
  );

  return { rails, reach };
}
